import { z } from "zod";
import {
    CREATOR_THEMES,
    CREATOR_EMAIL_RULES,
    SOCIAL_PROVIDERS,
    SOUL_PIPELINE_STATUSES_WITH_MISSING,
} from "@artha/types";
import { JobItemSchema } from "./jobs";

// Validation constants
const NAME_MIN_LENGTH = 1;
const NAME_MAX_LENGTH = 100;
const BIO_MAX_LENGTH = 160;
const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 30;
const NICHE_MAX_COUNT = 5;
const NICHE_MIN_LENGTH = 2;
const NICHE_MAX_LENGTH = 50;
const META_TITLE_MAX_LENGTH = 60;
const META_DESCRIPTION_MAX_LENGTH = 160;

export const UpdateSettingsSchema = z.object({
    jobFeedPreferences: z.object({
        positiveKeywords: z.array(z.string()).optional(),
        negativeKeywords: z.array(z.string()).optional(),
        countries: z.array(z.string()).optional(),
        workModes: z.array(z.string()).optional(),
        jobTypes: z.array(z.string()).optional(),
        industries: z.array(z.string()).optional(),
        categories: z.array(z.string()).optional(), // Move niche categories here
        companies: z.array(z.string()).optional(), // Company filter for employer tab
        minSalary: z.number().optional(),
        currencies: z.array(z.string()).optional(), // Change to array
        educationLevels: z.array(z.string()).optional(), // Change to array
        experienceLevels: z.array(z.string()).optional(), // Add as array
        redirectExpiredJobs: z.boolean().optional(),
    }).optional(),
    // Keep for backward compatibility, but prefer categories in jobFeedPreferences
    nicheCategories: z.array(z.string()).optional(),
});

export const UpdateAppearanceSchema = z.object({
    theme: z.enum(CREATOR_THEMES).optional(),
    backgroundColor: z.string().optional(),
    cardColor: z.string().optional(),
    titleColor: z.string().optional(),
    ctaColor: z.string().optional(),
    textColor: z.string().optional(),
});

export const UpdateSeoSchema = z.object({
    metaTitle: z.string()
        .max(META_TITLE_MAX_LENGTH, `Meta title must be at most ${META_TITLE_MAX_LENGTH} characters`)
        .optional(),
    metaDescription: z.string()
        .max(META_DESCRIPTION_MAX_LENGTH, `Meta description must be at most ${META_DESCRIPTION_MAX_LENGTH} characters`)
        .optional(),
    shareCardColor: z.string().optional(),
});

export const UpdateProfileSchema = z.object({
    displayName: z.string()
        .min(NAME_MIN_LENGTH, "Name is required")
        .max(NAME_MAX_LENGTH, `Name must be at most ${NAME_MAX_LENGTH} characters`)
        .optional(),
    bio: z.string()
        .max(BIO_MAX_LENGTH, `Bio must be at most ${BIO_MAX_LENGTH} characters`)
        .optional(),
    username: z.string().trim()
        .min(USERNAME_MIN_LENGTH, `Username must be at least ${USERNAME_MIN_LENGTH} characters`)
        .max(USERNAME_MAX_LENGTH, `Username must be at most ${USERNAME_MAX_LENGTH} characters`)
        .refine((value) => CREATOR_EMAIL_RULES.usernamePattern.test(value.toLowerCase()) && !CREATOR_EMAIL_RULES.reservedUsernames.has(value.toLowerCase()), "Use letters and numbers separated by single underscores or hyphens; system names are reserved")
        .optional(),
    nicheCategories: z.array(
        z.string()
            .min(NICHE_MIN_LENGTH, `Niche must be at least ${NICHE_MIN_LENGTH} characters`)
            .max(NICHE_MAX_LENGTH, `Niche must be at most ${NICHE_MAX_LENGTH} characters`)
    )
        .max(NICHE_MAX_COUNT, `Maximum ${NICHE_MAX_COUNT} niches allowed`)
        .optional(),
    isAnonymous: z.boolean().optional(),
    isLeaderboardAnonymous: z.boolean().optional(),
    timezone: z.string().optional(),
    whatsappNumber: z.string().trim()
        .regex(/^(\+[1-9]\d{7,14})?$/, "Enter a valid international WhatsApp number")
        .optional(),
    isWhatsAppSubscribed: z.boolean().optional(),
    bookCallClickedAt: z.string().datetime().optional(),
});

export const CheckUsernameSchema = z.object({
    username: z.string().trim()
        .min(USERNAME_MIN_LENGTH, `Username must be at least ${USERNAME_MIN_LENGTH} characters`)
        .max(USERNAME_MAX_LENGTH, `Username must be at most ${USERNAME_MAX_LENGTH} characters`)
        .refine((value) => CREATOR_EMAIL_RULES.usernamePattern.test(value.toLowerCase()) && !CREATOR_EMAIL_RULES.reservedUsernames.has(value.toLowerCase()), "Use letters and numbers separated by single underscores or hyphens; system names are reserved"),
});

export const UpdatePersonalInfoSchema = z.object({
    displayName: z.string()
        .min(NAME_MIN_LENGTH, "Name is required")
        .max(NAME_MAX_LENGTH, `Name must be at most ${NAME_MAX_LENGTH} characters`)
        .trim(),
    email: z.string()
        .email("Please enter a valid email address")
        .max(255, "Email must be at most 255 characters")
        .trim()
        .toLowerCase(),
});

export const CheckEmailAvailabilitySchema = z.object({
    email: z.string()
        .email("Please enter a valid email address")
        .max(255, "Email must be at most 255 characters")
        .trim()
        .toLowerCase(),
});

export const DeleteCreatorAccountSchema = z.object({
    confirmation: z.literal("DELETE"),
    reasons: z.array(z.string().min(1).max(100)).max(10),
    otherReason: z.string().trim().min(1).max(500).optional(),
}).superRefine((data, context) => {
    if (data.reasons.includes("other") && !data.otherReason) {
        context.addIssue({
            code: "custom",
            message: "Please provide the other reason",
            path: ["otherReason"],
        });
    }
});

export const AddSocialLinkSchema = z.object({
    platform: z.enum(SOCIAL_PROVIDERS),
    profileUrl: z.string().url(),
});

export const RemoveSocialLinkSchema = z.object({
    platform: z.enum(SOCIAL_PROVIDERS),
});

export const UploadAvatarResponseSchema = z.object({
    avatarUrl: z.string().url(),
});

export const FirstJobShareSchema = z.object({});

export const FirstJobShareResponseSchema = z.object({
    isFirst: z.boolean(),
});

export const FirstPostStudioShareSchema = z.object({});

export const FirstPostStudioShareResponseSchema = z.object({
    isFirst: z.boolean(),
});

export const BioLinkAddedSchema = z.object({});

export const BioLinkAddedResponseSchema = z.object({
    isFirst: z.boolean(),
});

export const BookCallClickedSchema = z.object({});

export const BookCallClickedResponseSchema = z.object({
    isFirst: z.boolean(),
});

export const JobShareClickSchema = z.object({});

export const JobShareClickResponseSchema = z.object({
    weeklyJobShareCount: z.number(),
    monthlyJobShareCount: z.number(),
});

export const JobShareSuccessSchema = z.object({});

export const JobShareSuccessResponseSchema = z.object({
    lastJobShareAt: z.string().datetime(),
});

export const CreateDraftProfileSchema = z.object({
    profileUrl: z.string().url("Must be a valid social profile URL"),
    timezone: z.string().optional(),
    countryCode: z.string().min(1, "countryCode is required"),
});

export const PostStudioPreferenceSchema = z.enum([
    "linkedin",
    "instagram",
    "youtube",
    "reel_short",
]);

export const PostStudioSoulStatusSchema = z.object({
    status: z.enum(SOUL_PIPELINE_STATUSES_WITH_MISSING),
    version: z.number().int().positive().optional(),
    generatedAt: z.string().datetime().optional(),
});

export const PostStudioBootstrapResponseSchema = z.object({
    jobs: z.array(JobItemSchema),
    isMonetizationEnabled: z.boolean(),
    firstJobShareAt: z.string().datetime().optional(),
    soulStatus: PostStudioSoulStatusSchema,
});

export const PostStudioGenerateRequestSchema = z.object({
    selectedJobIds: z.array(z.string().min(1)).min(1, "At least one job must be selected"),
    preference: PostStudioPreferenceSchema,
});

export const PostStudioReelShortOutputSchema = z.object({
    hook_on_screen: z.string().min(1),
    spoken_script: z.string().min(1),
    visual_direction: z.string().min(1),
    caption: z.string().min(1),
});

export const PostStudioGenerateResponseSchema = z.object({
    preference: PostStudioPreferenceSchema,
    contentText: z.string().optional(),
    reelShort: PostStudioReelShortOutputSchema.optional(),
    promptVersion: z.string(),
    soulVersion: z.number().int().positive().optional(),
});

export type UpdateSettingsDto = z.infer<typeof UpdateSettingsSchema>;
export type UpdateAppearanceDto = z.infer<typeof UpdateAppearanceSchema>;
export type UpdateSeoDto = z.infer<typeof UpdateSeoSchema>;
export type UpdateProfileDto = z.infer<typeof UpdateProfileSchema>;
export type UpdatePersonalInfoDto = z.infer<typeof UpdatePersonalInfoSchema>;
export type CheckUsernameDto = z.infer<typeof CheckUsernameSchema>;
export type CheckEmailAvailabilityDto = z.infer<typeof CheckEmailAvailabilitySchema>;
export type DeleteCreatorAccountDto = z.infer<typeof DeleteCreatorAccountSchema>;
export type AddSocialLinkDto = z.infer<typeof AddSocialLinkSchema>;
export type RemoveSocialLinkDto = z.infer<typeof RemoveSocialLinkSchema>;
export type UploadAvatarResponseDto = z.infer<typeof UploadAvatarResponseSchema>;
export type FirstJobShareDto = z.infer<typeof FirstJobShareSchema>;
export type FirstJobShareResponseDto = z.infer<typeof FirstJobShareResponseSchema>;
export type FirstPostStudioShareDto = z.infer<typeof FirstPostStudioShareSchema>;
export type FirstPostStudioShareResponseDto = z.infer<typeof FirstPostStudioShareResponseSchema>;
export type BioLinkAddedDto = z.infer<typeof BioLinkAddedSchema>;
export type BioLinkAddedResponseDto = z.infer<typeof BioLinkAddedResponseSchema>;
export type BookCallClickedDto = z.infer<typeof BookCallClickedSchema>;
export type BookCallClickedResponseDto = z.infer<typeof BookCallClickedResponseSchema>;
export type JobShareClickDto = z.infer<typeof JobShareClickSchema>;
export type JobShareClickResponseDto = z.infer<typeof JobShareClickResponseSchema>;
export type JobShareSuccessDto = z.infer<typeof JobShareSuccessSchema>;
export type JobShareSuccessResponseDto = z.infer<typeof JobShareSuccessResponseSchema>;
export type CreateDraftProfileDto = z.infer<typeof CreateDraftProfileSchema>;
export type PostStudioPreferenceDto = z.infer<typeof PostStudioPreferenceSchema>;
export type PostStudioSoulStatusDto = z.infer<typeof PostStudioSoulStatusSchema>;
export type PostStudioBootstrapResponseDto = z.infer<typeof PostStudioBootstrapResponseSchema>;
export type PostStudioGenerateRequestDto = z.infer<typeof PostStudioGenerateRequestSchema>;
export type PostStudioReelShortOutputDto = z.infer<typeof PostStudioReelShortOutputSchema>;
export type PostStudioGenerateResponseDto = z.infer<typeof PostStudioGenerateResponseSchema>;
